import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faFileArrowUp,
  faCopy,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { authFetch, DOCUMENT_TYPE_LABELS, DOCUMENT_DISPLAY_ORDER } from '../../utils/http';
import { learnerships, shortProgrammes } from '../../data/mockData';

// Document type id -> multipart field name the server expects.
const FIELD_NAMES: Record<number, string> = {
  0: 'IdDocument',
  1: 'MatricCertificate',
  2: 'Affidavit',
  3: 'Cv',
};

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT = ['.pdf', '.jpg', '.jpeg', '.png'];

const inputClass =
  'w-full px-4 py-2.5 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white';

interface Created {
  email: string;
  password: string;
}

/** Public online application: details, programme choice and the four required documents. */
export default function ApplicationForm() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    idNumber: '',
    dateOfBirth: '',
    address: '',
    programme: '',
    motivation: '',
  });
  const [files, setFiles] = useState<Record<number, File | null>>({ 0: null, 1: null, 2: null, 3: null });
  const [fileErrors, setFileErrors] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);
  const [copied, setCopied] = useState(false);

  const set = (name: string, value: string) => setForm((prev) => ({ ...prev, [name]: value }));

  const pickFile = (type: number, file: File | null) => {
    setError('');
    if (!file) return;
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setFileErrors((p) => ({ ...p, [type]: 'Use a PDF, JPG or PNG file.' }));
      setFiles((p) => ({ ...p, [type]: null }));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileErrors((p) => ({ ...p, [type]: 'This file is larger than 10 MB.' }));
      setFiles((p) => ({ ...p, [type]: null }));
      return;
    }
    setFileErrors((p) => ({ ...p, [type]: '' }));
    setFiles((p) => ({ ...p, [type]: file }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim() || !form.lastName.trim()) return setError('Please enter your first name and surname.');
    if (!form.email.trim() || !form.email.includes('@')) return setError('Please enter a valid email address.');
    if (!form.phoneNumber.trim()) return setError('Please enter your phone number.');
    if (!form.programme) return setError('Please choose the programme you are applying for.');

    const missing = DOCUMENT_DISPLAY_ORDER.filter((t) => !files[t]).map((t) => DOCUMENT_TYPE_LABELS[t]);
    if (missing.length > 0) {
      return setError(
        missing.length === 1
          ? `A required document has not been supplied: ${missing[0]}.`
          : `Required documents have not been supplied: ${missing.join('; ')}.`
      );
    }

    const body = new FormData();
    body.append('FirstName', form.firstName.trim());
    body.append('LastName', form.lastName.trim());
    body.append('Email', form.email.trim());
    body.append('PhoneNumber', form.phoneNumber.trim());
    body.append('IdNumber', form.idNumber.trim());
    if (form.dateOfBirth) body.append('DateOfBirth', form.dateOfBirth);
    body.append('Address', form.address.trim());
    body.append('Programme', form.programme);
    body.append('Motivation', form.motivation.trim());
    DOCUMENT_DISPLAY_ORDER.forEach((t) => body.append(FIELD_NAMES[t], files[t] as File));

    setIsSubmitting(true);
    const res = await authFetch(null, '/api/applications', { method: 'POST', form: body });
    setIsSubmitting(false);

    if (!res.ok) {
      // 413 comes from the web server before our API sees the request.
      setError(res.status === 413 ? 'Your files are too large. Each file must be 10 MB or smaller.' : res.message);
      return;
    }

    setCreated({ email: res.data?.data?.email ?? form.email.trim(), password: res.data?.data?.password ?? '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const copyPassword = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be blocked; the password is still shown on screen.
    }
  };

  if (created) {
    return (
      <div className="card-surface p-8 max-w-2xl mx-auto">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faCheckCircle} className="h-8 w-8 text-green-600" />
          <h2 className="text-2xl font-bold text-brand-950">Application received</h2>
        </div>
        <p className="mt-4 text-sm text-brand-900/70">
          Thank you. Your application and documents are with our admin team. We will review them and you can sign in once
          you are accepted.
        </p>

        <div className="mt-6 rounded-2xl bg-canvas-100 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-900/50">Your sign-in details</p>
          <p className="mt-3 text-sm text-brand-900/70">Email</p>
          <p className="font-semibold text-brand-950 break-all">{created.email}</p>
          <p className="mt-3 text-sm text-brand-900/70">Password</p>
          <div className="flex items-center gap-3 flex-wrap">
            <code className="text-lg font-bold tracking-wider text-brand-950 select-all">{created.password}</code>
            <button
              type="button"
              onClick={copyPassword}
              className="px-3 py-1.5 border border-canvas-300 bg-white text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-50 flex items-center gap-1.5"
            >
              <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} className="h-3 w-3" />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Save this password now. It is shown only once. It will not work until your application is accepted. You can
            change it after your first sign-in.
          </p>
        </div>

        <div className="mt-6">
          <Link to="/" className="text-sm font-semibold text-brand-700 underline underline-offset-4">
            Back to the home page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-surface p-6 sm:p-8 max-w-3xl mx-auto space-y-8" noValidate>
      <div>
        <h2 className="text-2xl font-bold text-brand-950">Apply online</h2>
        <p className="mt-2 text-sm text-brand-900/65">
          Fill in your details, choose a programme and upload the four documents below. It takes about five minutes.
        </p>
      </div>

      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-brand-600">1. About you</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-brand-950 mb-2">First name *</label>
            <input className={inputClass} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} autoComplete="given-name" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-brand-950 mb-2">Surname *</label>
            <input className={inputClass} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} autoComplete="family-name" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-brand-950 mb-2">Email address *</label>
            <input type="email" className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-brand-950 mb-2">Phone number *</label>
            <input type="tel" className={inputClass} value={form.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} autoComplete="tel" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-brand-950 mb-2">ID number</label>
            <input className={inputClass} value={form.idNumber} onChange={(e) => set('idNumber', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-brand-950 mb-2">Date of birth</label>
            <input type="date" className={inputClass} value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-brand-950 mb-2">Home address</label>
          <textarea rows={2} className={`${inputClass} resize-none`} value={form.address} onChange={(e) => set('address', e.target.value)} />
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-brand-600">2. Programme</h3>
        <div>
          <label className="block text-sm font-semibold text-brand-950 mb-2">Programme you are applying for *</label>
          <select className={inputClass} value={form.programme} onChange={(e) => set('programme', e.target.value)}>
            <option value="">Choose a programme...</option>
            <optgroup label="Learnerships (12 months)">
              {learnerships.map((l) => (
                <option key={l.id} value={l.title}>
                  {l.title}
                </option>
              ))}
            </optgroup>
            <optgroup label="Short programmes (4 months)">
              {shortProgrammes.map((p) => (
                <option key={p.id} value={p.title}>
                  {p.title}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-brand-950 mb-2">Why do you want to join? (optional)</label>
          <textarea rows={3} maxLength={1500} className={`${inputClass} resize-none`} value={form.motivation} onChange={(e) => set('motivation', e.target.value)} />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-brand-600">3. Documents (all four required)</h3>
        <p className="text-xs text-brand-900/55">PDF, JPG or PNG, up to 10 MB each. Only our admin team can see them.</p>
        {DOCUMENT_DISPLAY_ORDER.map((type) => {
          const file = files[type];
          return (
            <div key={type} className="border border-canvas-300 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-brand-950">{DOCUMENT_TYPE_LABELS[type]} *</p>
                  <p className={`text-xs truncate ${file ? 'text-green-700' : 'text-brand-900/50'}`}>
                    {file ? file.name : 'No file chosen'}
                  </p>
                </div>
                <label className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors cursor-pointer flex items-center gap-1.5">
                  <FontAwesomeIcon icon={file ? faCheckCircle : faFileArrowUp} className="h-3 w-3" />
                  {file ? 'Change' : 'Choose file'}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      e.target.value = '';
                      pickFile(type, f);
                    }}
                  />
                </label>
              </div>
              {fileErrors[type] && <p className="mt-2 text-xs text-red-700">{fileErrors[type]}</p>}
            </div>
          );
        })}
      </section>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" role="alert">
          <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-7 py-3 bg-brand-700 text-white font-semibold rounded-full shadow-float hover:bg-brand-800 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {isSubmitting && <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />}
          {isSubmitting ? 'Submitting...' : 'Submit application'}
        </button>
        <p className="text-xs text-brand-900/50">We generate your password when you submit.</p>
      </div>
    </form>
  );
}
